export const uploaderStyles = `
/* ─── Host tokens ───────────────────────────────────────────── */
:host {
  --tiram-uploader-font-family: Inter, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
  --tiram-uploader-bg: #ffffff;
  --tiram-uploader-fg: #101828;
  --tiram-uploader-muted-fg: #475467;
  --tiram-uploader-border: #d0d5dd;
  --tiram-uploader-border-strong: #98a2b3;
  --tiram-uploader-surface: #f9fafb;
  --tiram-uploader-primary: #1570ef;
  --tiram-uploader-primary-hover: #175cd3;
  --tiram-uploader-primary-light: #eff8ff;
  --tiram-uploader-success: #039855;
  --tiram-uploader-success-light: #ecfdf3;
  --tiram-uploader-error: #d92d20;
  --tiram-uploader-error-light: #fef3f2;
  --tiram-uploader-warning: #dc6803;
  --tiram-uploader-warning-light: #fffaeb;
  --tiram-uploader-radius: 12px;
  --tiram-uploader-file-radius: 10px;
  --tiram-uploader-padding: 20px;
  --tiram-uploader-gap: 12px;
  --tiram-uploader-shadow: 0 12px 16px -4px rgba(16, 24, 40, 0.10), 0 4px 6px -2px rgba(16, 24, 40, 0.05);
  --tiram-uploader-focus-ring: 0 0 0 4px rgba(46, 144, 250, 0.32);
  --tiram-uploader-progress-bg: #eaecf0;
  --tiram-uploader-progress-fg: #1570ef;
  --tiram-uploader-thumbnail-size: 48px;
  --tiram-uploader-motion-duration: 160ms;
  --tiram-uploader-motion-easing: cubic-bezier(0.2, 0, 0, 1);
  color-scheme: light;
  display: block;
  max-width: 100%;
  min-width: 0;
  color: var(--tiram-uploader-fg);
  font-family: var(--tiram-uploader-font-family);
  font-size: 14px;
  line-height: 1.5;
  -webkit-font-smoothing: antialiased;
}

/* ─── Dark theme ────────────────────────────────────────────── */
:host([data-theme="dark"]) {
  --tiram-uploader-bg: #101828;
  --tiram-uploader-fg: #f9fafb;
  --tiram-uploader-muted-fg: #d0d5dd;
  --tiram-uploader-border: #344054;
  --tiram-uploader-border-strong: #667085;
  --tiram-uploader-surface: #1d2939;
  --tiram-uploader-primary-light: #102a56;
  --tiram-uploader-success-light: #053321;
  --tiram-uploader-error-light: #55160c;
  --tiram-uploader-warning-light: #4e1d09;
  --tiram-uploader-progress-bg: #344054;
  --tiram-uploader-focus-ring: 0 0 0 4px rgba(132, 202, 255, 0.36);
  --tiram-uploader-shadow: 0 16px 36px rgba(0, 0, 0, 0.34);
  color-scheme: dark;
}

/* ─── Root container ────────────────────────────────────────── */
.tiram-uploader {
  display: grid;
  gap: var(--tiram-uploader-gap);
  min-width: 0;
}

/* ─── Dropzone ──────────────────────────────────────────────── */
.tiram-uploader-dropzone {
  position: relative;
  display: grid;
  place-items: center;
  min-height: 172px;
  padding: var(--tiram-uploader-padding);
  color: var(--tiram-uploader-fg);
  background: var(--tiram-uploader-bg);
  border: 1.5px dashed var(--tiram-uploader-border);
  border-radius: var(--tiram-uploader-radius);
  box-shadow: 0 1px 2px rgba(16, 24, 40, 0.05);
  text-align: center;
  cursor: pointer;
  transition: border-color var(--tiram-uploader-motion-duration) var(--tiram-uploader-motion-easing),
              background var(--tiram-uploader-motion-duration) var(--tiram-uploader-motion-easing),
              box-shadow var(--tiram-uploader-motion-duration) var(--tiram-uploader-motion-easing);
}

.tiram-uploader-dropzone:hover,
.tiram-uploader-dropzone[data-dragging="true"] {
  border-color: var(--tiram-uploader-primary);
  background: var(--tiram-uploader-primary-light);
  box-shadow: var(--tiram-uploader-shadow);
}

.tiram-uploader-dropzone[data-dragging="true"] {
  border-style: solid;
  border-width: 2px;
}

.tiram-uploader-dropzone:focus-visible {
  outline: none;
  box-shadow: var(--tiram-uploader-focus-ring);
  border-color: var(--tiram-uploader-primary);
}

.tiram-uploader-dropzone[data-disabled="true"] {
  opacity: 0.48;
  cursor: not-allowed;
  pointer-events: none;
  box-shadow: none;
}

/* ─── Dropzone icon ─────────────────────────────────────────── */
.tiram-uploader-icon {
  width: 44px;
  height: 44px;
  display: inline-grid;
  place-items: center;
  margin: 0 auto 12px;
  color: var(--tiram-uploader-primary);
  background: var(--tiram-uploader-primary-light);
  border-radius: 999px;
}

.tiram-uploader-icon svg {
  width: 24px;
  height: 24px;
}

/* ─── Dropzone label & hint ─────────────────────────────────── */
.tiram-uploader-label {
  margin: 0;
  font-size: 15px;
  font-weight: 600;
  line-height: 22px;
}

.tiram-uploader-label span {
  color: var(--tiram-uploader-primary);
  text-decoration: underline;
  text-decoration-color: transparent;
  text-underline-offset: 2px;
  transition: text-decoration-color var(--tiram-uploader-motion-duration);
}

.tiram-uploader-dropzone:hover .tiram-uploader-label span {
  text-decoration-color: var(--tiram-uploader-primary);
}

.tiram-uploader-hint {
  margin: 6px 0 0;
  color: var(--tiram-uploader-muted-fg);
  font-size: 13px;
  line-height: 18px;
}

/* ─── Hidden file input ─────────────────────────────────────── */
.tiram-uploader-input {
  position: absolute;
  width: 1px;
  height: 1px;
  opacity: 0;
  pointer-events: none;
}

/* ─── Toolbar ───────────────────────────────────────────────── */
.tiram-uploader-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  flex-wrap: wrap;
  min-width: 0;
}

.tiram-uploader-summary {
  color: var(--tiram-uploader-muted-fg);
  font-size: 13px;
}

.tiram-uploader-actions {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

/* ─── Buttons ───────────────────────────────────────────────── */
.tiram-uploader-button,
.tiram-uploader-file-button {
  appearance: none;
  min-height: 34px;
  padding: 0 12px;
  border: 1px solid var(--tiram-uploader-border);
  border-radius: 8px;
  color: var(--tiram-uploader-fg);
  background: var(--tiram-uploader-bg);
  font: inherit;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: background var(--tiram-uploader-motion-duration) var(--tiram-uploader-motion-easing),
              border-color var(--tiram-uploader-motion-duration) var(--tiram-uploader-motion-easing),
              color var(--tiram-uploader-motion-duration) var(--tiram-uploader-motion-easing),
              box-shadow var(--tiram-uploader-motion-duration) var(--tiram-uploader-motion-easing);
}

.tiram-uploader-button[data-variant="primary"] {
  color: #ffffff;
  background: var(--tiram-uploader-primary);
  border-color: var(--tiram-uploader-primary);
}

.tiram-uploader-button:hover,
.tiram-uploader-file-button:hover {
  border-color: var(--tiram-uploader-border-strong);
  background: var(--tiram-uploader-surface);
}

.tiram-uploader-button[data-variant="primary"]:hover {
  background: var(--tiram-uploader-primary-hover);
  border-color: var(--tiram-uploader-primary-hover);
}

.tiram-uploader-button:focus-visible,
.tiram-uploader-file-button:focus-visible {
  outline: none;
  box-shadow: var(--tiram-uploader-focus-ring);
}

.tiram-uploader-button:disabled,
.tiram-uploader-file-button:disabled {
  opacity: 0.48;
  cursor: not-allowed;
}

/* ─── File list ─────────────────────────────────────────────── */
.tiram-uploader-list {
  display: grid;
  gap: 8px;
  margin: 0;
  padding: 0;
  list-style: none;
}

/* ─── File row ──────────────────────────────────────────────── */
.tiram-uploader-file {
  display: grid;
  grid-template-columns: var(--tiram-uploader-thumbnail-size) minmax(0, 1fr) auto;
  gap: 12px;
  align-items: center;
  padding: 10px;
  background: var(--tiram-uploader-bg);
  border: 1px solid var(--tiram-uploader-border);
  border-radius: var(--tiram-uploader-file-radius);
  transition: border-color var(--tiram-uploader-motion-duration) var(--tiram-uploader-motion-easing);
}

.tiram-uploader-file[data-status="success"] {
  border-color: var(--tiram-uploader-success);
  background: var(--tiram-uploader-success-light);
}

.tiram-uploader-file[data-status="error"] {
  border-color: var(--tiram-uploader-error);
  background: var(--tiram-uploader-error-light);
}

/* ─── Thumbnail ─────────────────────────────────────────────── */
.tiram-uploader-file-thumbnail {
  width: var(--tiram-uploader-thumbnail-size);
  height: var(--tiram-uploader-thumbnail-size);
  display: grid;
  place-items: center;
  overflow: hidden;
  color: var(--tiram-uploader-muted-fg);
  background: var(--tiram-uploader-surface);
  border-radius: 8px;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.02em;
}

.tiram-uploader-file-thumbnail img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

/* ─── File meta ─────────────────────────────────────────────── */
.tiram-uploader-file-main {
  min-width: 0;
}

.tiram-uploader-file-name {
  margin: 0;
  overflow: hidden;
  color: var(--tiram-uploader-fg);
  font-size: 14px;
  font-weight: 600;
  line-height: 20px;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.tiram-uploader-file-meta,
.tiram-uploader-file-error {
  margin: 2px 0 0;
  color: var(--tiram-uploader-muted-fg);
  font-size: 12px;
  line-height: 17px;
}

.tiram-uploader-file-error {
  color: var(--tiram-uploader-error);
  font-weight: 500;
}

/* ─── Status badges ─────────────────────────────────────────── */
.tiram-uploader-file-status {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  margin-left: 6px;
  font-size: 12px;
  font-weight: 600;
}

.tiram-uploader-file-status[data-status="success"] { color: var(--tiram-uploader-success); }
.tiram-uploader-file-status[data-status="error"] { color: var(--tiram-uploader-error); }
.tiram-uploader-file-status[data-status="uploading"] { color: var(--tiram-uploader-primary); }
.tiram-uploader-file-status[data-status="paused"] { color: var(--tiram-uploader-warning); }

/* ─── Status icon dots ──────────────────────────────────────── */
.tiram-uploader-file-status::before {
  content: '';
  width: 7px;
  height: 7px;
  border-radius: 999px;
  background: currentColor;
  flex-shrink: 0;
}

.tiram-uploader-file-status[data-status="uploading"]::before {
  animation: tiram-uploader-pulse 1s ease-in-out infinite;
}

/* ─── Progress bar ──────────────────────────────────────────── */
.tiram-uploader-progress {
  height: 5px;
  margin-top: 8px;
  overflow: hidden;
  background: var(--tiram-uploader-progress-bg);
  border-radius: 999px;
}

.tiram-uploader-progress-bar {
  width: calc(var(--progress, 0) * 100%);
  height: 100%;
  background: var(--tiram-uploader-progress-fg);
  border-radius: inherit;
  transition: width 120ms linear;
}

.tiram-uploader-progress-bar[data-indeterminate="true"] {
  width: 40%;
  animation: tiram-uploader-indeterminate 1.05s ease-in-out infinite;
}

.tiram-uploader-file[data-status="success"] .tiram-uploader-progress-bar {
  background: var(--tiram-uploader-success);
}

.tiram-uploader-file[data-status="error"] .tiram-uploader-progress-bar {
  background: var(--tiram-uploader-error);
}

/* ─── File action buttons ───────────────────────────────────── */
.tiram-uploader-file-actions {
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

.tiram-uploader-file-button {
  min-height: 30px;
  padding: 0 9px;
  font-size: 12px;
}

.tiram-uploader-file-button[data-action="remove"] {
  color: var(--tiram-uploader-error);
  border-color: transparent;
  background: transparent;
}

.tiram-uploader-file-button[data-action="remove"]:hover {
  background: var(--tiram-uploader-error-light);
  border-color: var(--tiram-uploader-error);
}

/* ─── Empty state ───────────────────────────────────────────── */
.tiram-uploader-empty {
  padding: 14px;
  color: var(--tiram-uploader-muted-fg);
  background: var(--tiram-uploader-surface);
  border: 1px dashed var(--tiram-uploader-border);
  border-radius: var(--tiram-uploader-file-radius);
  font-size: 13px;
  text-align: center;
}

/* ─── Screen-reader only ────────────────────────────────────── */
.tiram-uploader-sr {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}

/* ─── Density: compact ──────────────────────────────────────── */
:host([data-density="compact"]) {
  --tiram-uploader-padding: 14px;
  --tiram-uploader-thumbnail-size: 40px;
  --tiram-uploader-gap: 8px;
}

:host([data-density="compact"]) .tiram-uploader-dropzone {
  min-height: 140px;
}

:host([data-density="compact"]) .tiram-uploader-button,
:host([data-density="compact"]) .tiram-uploader-file-button {
  min-height: 30px;
  padding: 0 10px;
  font-size: 12px;
}

/* ─── Density: spacious ─────────────────────────────────────── */
:host([data-density="spacious"]) {
  --tiram-uploader-padding: 26px;
  --tiram-uploader-thumbnail-size: 56px;
  --tiram-uploader-gap: 16px;
}

:host([data-density="spacious"]) .tiram-uploader-dropzone {
  min-height: 200px;
}

:host([data-density="spacious"]) .tiram-uploader-button {
  min-height: 40px;
  padding: 0 16px;
}

/* ─── Animations ────────────────────────────────────────────── */
@keyframes tiram-uploader-indeterminate {
  from { transform: translateX(-120%); }
  to { transform: translateX(260%); }
}

@keyframes tiram-uploader-pulse {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.35; }
}

/* ─── System dark mode ──────────────────────────────────────── */
@media (prefers-color-scheme: dark) {
  :host([data-theme="system"]) {
    --tiram-uploader-bg: #101828;
    --tiram-uploader-fg: #f9fafb;
    --tiram-uploader-muted-fg: #d0d5dd;
    --tiram-uploader-border: #344054;
    --tiram-uploader-border-strong: #667085;
    --tiram-uploader-surface: #1d2939;
    --tiram-uploader-primary-light: #102a56;
    --tiram-uploader-success-light: #053321;
    --tiram-uploader-error-light: #55160c;
    --tiram-uploader-warning-light: #4e1d09;
    --tiram-uploader-progress-bg: #344054;
    --tiram-uploader-focus-ring: 0 0 0 4px rgba(132, 202, 255, 0.36);
    --tiram-uploader-shadow: 0 16px 36px rgba(0, 0, 0, 0.34);
    color-scheme: dark;
  }
}

/* ─── Reduced motion ────────────────────────────────────────── */
@media (prefers-reduced-motion: reduce) {
  .tiram-uploader-dropzone,
  .tiram-uploader-button,
  .tiram-uploader-file-button,
  .tiram-uploader-progress-bar,
  .tiram-uploader-file,
  .tiram-uploader-label span {
    transition-duration: 1ms !important;
    animation-duration: 1ms !important;
  }
}

/* ─── Forced colors / high contrast ─────────────────────────── */
@media (forced-colors: active) {
  .tiram-uploader-dropzone {
    border-color: CanvasText;
  }

  .tiram-uploader-file {
    border-color: CanvasText;
  }

  .tiram-uploader-button,
  .tiram-uploader-file-button {
    border-color: ButtonText;
    color: ButtonText;
    background: ButtonFace;
  }

  .tiram-uploader-button[data-variant="primary"] {
    background: Highlight;
    color: HighlightText;
    border-color: Highlight;
  }

  .tiram-uploader-progress-bar {
    background: Highlight;
  }

  .tiram-uploader-progress {
    background: ButtonFace;
    border: 1px solid CanvasText;
  }

  .tiram-uploader-dropzone:focus-visible {
    outline: 2px solid Highlight;
    outline-offset: 2px;
  }

  .tiram-uploader-button:focus-visible,
  .tiram-uploader-file-button:focus-visible {
    outline: 2px solid Highlight;
    outline-offset: 2px;
    box-shadow: none;
  }

  .tiram-uploader-file-status::before {
    forced-color-adjust: none;
  }
}

/* ─── Responsive: mobile ────────────────────────────────────── */
@media (max-width: 560px) {
  .tiram-uploader-dropzone {
    min-height: 148px;
  }

  .tiram-uploader-toolbar {
    flex-direction: column;
    align-items: stretch;
  }

  .tiram-uploader-actions {
    width: 100%;
  }

  .tiram-uploader-button {
    flex: 1 1 0;
  }

  .tiram-uploader-file {
    grid-template-columns: var(--tiram-uploader-thumbnail-size) minmax(0, 1fr);
  }

  .tiram-uploader-file-actions {
    grid-column: 1 / -1;
    justify-content: flex-end;
  }
}

/* ─── RTL ───────────────────────────────────────────────────── */
:host([dir="rtl"]) .tiram-uploader-file-status {
  margin-left: 0;
  margin-right: 6px;
}

/* ─── Print ─────────────────────────────────────────────────── */
@media print {
  .tiram-uploader-dropzone,
  .tiram-uploader-toolbar,
  .tiram-uploader-file-actions {
    display: none !important;
  }

  .tiram-uploader-file {
    break-inside: avoid;
    box-shadow: none;
  }
}
`;
